import React, { useContext, useState } from "react";
import { Modal, Select, Row, Col, Form, Upload, Radio } from "antd";
import { useQuery } from "react-query";
import stateDistrictMapping from "../../assets/data/Indian_district_State.json";
import statePCMapping from "../../assets/data/state_pc_mapping.json";
import ApprovaContext from "../../context/approvalContext";
import TextArea from "antd/es/input/TextArea";
import uploadCloud from "../../assets/svg/uploadCloud.svg";
import { FileFilled } from "@ant-design/icons";
import { AiOutlineClose } from "react-icons/ai";
import useAxiosInstance from "../../lib/useAxiosInstance";
import { ToastContainer, toast } from "react-toastify";

const { Option } = Select;

const AddQuantitativeSurvey = ({ isOpen, setShowQuantitativeSurvey }) => {
  const [form] = Form.useForm();
  const axiosInstance = useAxiosInstance();
  const approvaContext = useContext(ApprovaContext);
  const [radioValue, setRadioValue] = useState("assembly");
  const [fileList, setFileList] = useState([]);
  const [excelFile, setExcelFile] = useState(null);
  const [uploadingExcel, setUploadingExcel] = useState(false);

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let year = 1951; year <= currentYear; year++) {
    years.push(year);
  }

  const [inputValues, setInputValues] = useState({
    clientId: null,
    state: null,
    district: null,
    constituency: null,
    pcName: null,
    year: null,
    description: "",
    boundaries: [],
  });

  const closeModal = () => {
    approvaContext.updateOpenModal(false);
    form.resetFields();
    setShowQuantitativeSurvey(false);
    handleResetFile();
    setExcelFile(null);
    setInputValues({
      clientId: null,
      state: null,
      district: null,
      constituency: null,
      pcName: null,
      year: null,
      description: "",
      boundaries: [],
    });
  };

  const handleResetFile = () => {
    setInputValues((prev) => ({ ...prev, boundaries: [] }));
    setFileList([]);
  };

  // Fetch clients with showQuantitativeSurvey = true
  const { data: clientOptions = [] } = useQuery(
    ["quantitative-survey-clients"],
    async () => {
      const res = await axiosInstance.get("api/quantitativeSurvey/clients");
      return (res?.data?.data || []).map((c) => ({
        value: c._id,
        label: `${c.adminFirstName || ""} ${c.adminLastName || ""}`.trim(),
      }));
    },
    { refetchOnWindowFocus: false, staleTime: Infinity, cacheTime: Infinity }
  );
  console.log("clientOptions",clientOptions)
  const stateOptions = Object.keys(stateDistrictMapping).map((state) => ({
    value: state,
    label: state,
  }));

  const districtOptions = inputValues.state
    ? stateDistrictMapping[inputValues.state].map((d) => ({ value: d, label: d }))
    : [];

  const pcOptions = inputValues.state
    ? statePCMapping[inputValues.state].map((pc) => ({ value: pc, label: pc }))
    : [];

  const handleChange = (value, name) => {
    if (name === "state") {
      setInputValues((prev) => ({
        ...prev,
        state: value,
        district: null,
        constituency: null,
        pcName: null,
      }));
      form.setFieldsValue({ district: undefined, constituency: undefined, pcName: undefined });
      return;
    }
    if (name === "district") {
      setInputValues((prev) => ({ ...prev, district: value, constituency: null }));
      form.setFieldsValue({ constituency: undefined });
      return;
    }
    setInputValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleRadioChange = (e) => {
    const value = e.target.value;
    setRadioValue(value);

    if (value === "assembly") {
      setInputValues((prev) => ({ ...prev, pcName: null }));
      form.setFieldsValue({ pcName: undefined });
    }
    if (value === "parliamentary") {
      setInputValues((prev) => ({ ...prev, district: null, constituency: null }));
      form.setFieldsValue({ district: undefined, constituency: undefined });
    }
    if (value === "state") {
      setInputValues((prev) => ({ ...prev, district: null, constituency: null, pcName: null }));
      form.setFieldsValue({ district: undefined, constituency: undefined, pcName: undefined });
    }
  };

  const fetchConstituencyByStateDistrict = async ({ queryKey }) => {
    const [, state, district] = queryKey;
    if (!state || !district) return [];
    const response = await axiosInstance.get(
      `api/constituency/getAllConstituency?state=${state}&district=${district}`
    );
    return response?.data?.data?.data || response?.data?.data || [];
  };

  const { data: constituencyList = [], isLoading: isConstituencyLoading } = useQuery(
    ["qs-constituencies-by-state-district", inputValues.state, inputValues.district],
    fetchConstituencyByStateDistrict,
    {
      enabled: !!inputValues.state && !!inputValues.district,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  const constituencyOptions = constituencyList.map((item) => ({
    value: item?.name,
    label: item?.name || item?.constituency_name || item?.constituency || String(item),
  }));

  const handleGeoFileUpload = async (data) => {
    try {
      const response = await axiosInstance.post(
        `/api/quantitativeSurvey/createShape`,
        data,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Quantitative Survey Shape Uploaded Successfully");
      closeModal();
      return response?.data?.data;
    } catch (error) {
      console.error("GeoJSON upload failed:", error.message);
      toast.error("File upload failed");
      return null;
    }
  };

  const handleExcelUpload = async () => {
    try {
      if (!excelFile) {
        toast.error("Please select an Excel file");
        return;
      }

      if (!inputValues.state || !inputValues.year) {
        toast.error("State and Year are required");
        return;
      }

      setUploadingExcel(true);
      const formData = new FormData();
      formData.append("csvFile", excelFile);
      formData.append("clientId", inputValues.clientId || "");
      formData.append("state", inputValues.state);
      formData.append("district", inputValues.district || "");
      formData.append("constituency", inputValues.constituency || "");
      formData.append("pcName", inputValues.pcName || "");
      formData.append("shapeType", radioValue);
      formData.append("year", inputValues.year);

      await axiosInstance.post("/api/quantitativeSurvey/uploadExcel", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Excel uploaded successfully");
      setExcelFile(null);
    } catch (error) {
      console.error(error);
      toast.error("Excel upload failed");
    } finally {
      setUploadingExcel(false);
    }
  };

  const handleSubmitForm = () => {
    const data = {
      clientId: inputValues?.clientId,
      state: inputValues?.state,
      district: radioValue === "assembly" ? inputValues?.district : null,
      constituency: radioValue === "assembly" ? inputValues?.constituency : null,
      pcName: radioValue === "parliamentary" ? inputValues?.pcName : null,
      year: inputValues?.year,
      description: inputValues?.description,
      boundaries: inputValues?.boundaries[0]?.originFileObj,
      shapeType: radioValue,
    };

    handleGeoFileUpload(data);
  };

  return (
    <>
      {isOpen && (
        <Modal
          className="add-user-modal"
          open={isOpen}
          onCancel={closeModal}
          width={1040}
          maskClosable={false}
          footer={[
            <button
              key="add-quantitative-shape"
              form="quantitative-survey-form"
              type="submit"
              className="add-user-btn"
              style={{ width: "auto" }}
            >
              Add Quantitative Survey Shape
            </button>,
          ]}
        >
          <ToastContainer />
          <Form
            form={form}
            id="quantitative-survey-form"
            onFinish={handleSubmitForm}
            className="modal-body-container"
          >
            <div className="user-info-modal">
              <div className="modal-title">
                <h3>Add Quantitative Survey</h3>
              </div>
            </div>
            <div className="modal-wrapper-body constituency-form">
              <div className="model-wrapper">
                <div className="modal-wrapper-content">
                  <h4>Quantitative Survey Information</h4>

                  {/* Client Select */}
                  <Row className="add-modal-row mt-3 mb-2">
                    <Col span={10}>
                      <label>
                        Select Client<span className="required-field">*</span>
                      </label>
                      <Form.Item
                        name="clientId"
                        rules={[{ required: true, message: "Please Select a Client" }]}
                      >
                        <Select
                          showSearch
                          onChange={(value) => handleChange(value, "clientId")}
                          options={clientOptions}
                          optionFilterProp="label"
                          filterOption={(input, option) =>
                            option.label.toLowerCase().includes(input.toLowerCase())
                          }
                          placeholder="Select Client"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Radio.Group onChange={handleRadioChange} value={radioValue}>
                    <Radio value="assembly">Assembly</Radio>
                    <Radio value="parliamentary">Parliamentary</Radio>
                    <Radio value="state">State</Radio>
                  </Radio.Group>

                  <Row className="add-modal-row mt-5 mb-2">
                    <Col span={8}>
                      <label>
                        Select State<span className="required-field">*</span>
                      </label>
                      <Form.Item
                        name="state"
                        rules={[{ required: true, message: "Please Select State" }]}
                      >
                        <Select
                          showSearch
                          onChange={(value) => handleChange(value, "state")}
                          options={stateOptions}
                          optionFilterProp="label"
                          filterOption={(input, option) =>
                            option.label.toLowerCase().includes(input.toLowerCase())
                          }
                        />
                      </Form.Item>
                    </Col>

                    {radioValue === "assembly" && (
                      <Col span={8} offset={2}>
                        <label>
                          Select District<span className="required-field">*</span>
                        </label>
                        <Form.Item
                          name="district"
                          rules={[{ required: true, message: "Please Select District" }]}
                        >
                          <Select
                            showSearch
                            onChange={(value) => handleChange(value, "district")}
                            options={districtOptions}
                            disabled={!inputValues.state}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                              option.label.toLowerCase().includes(input.toLowerCase())
                            }
                            placeholder={
                              inputValues.state ? "Select District" : "Please select state first"
                            }
                          />
                        </Form.Item>
                      </Col>
                    )}

                    {radioValue === "assembly" && (
                      <Col span={8} offset={2}>
                        <label>
                          Select Constituency<span className="required-field">*</span>
                        </label>
                        <Form.Item
                          name="constituency"
                          rules={[{ required: true, message: "Please Select Constituency" }]}
                        >
                          <Select
                            showSearch
                            onChange={(value) => handleChange(value, "constituency")}
                            options={constituencyOptions}
                            disabled={!inputValues.state || !inputValues.district}
                            loading={isConstituencyLoading}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                              option.label.toLowerCase().includes(input.toLowerCase())
                            }
                            placeholder={
                              inputValues.state && inputValues.district
                                ? "Select Constituency"
                                : "Please select state and district first"
                            }
                          />
                        </Form.Item>
                      </Col>
                    )}

                    {radioValue === "parliamentary" && (
                      <Col span={8} offset={2}>
                        <label>
                          Select Parliamentary<span className="required-field">*</span>
                        </label>
                        <Form.Item
                          name="pcName"
                          rules={[{ required: true, message: "Please Select Parliamentary" }]}
                        >
                          <Select
                            showSearch
                            onChange={(value) => handleChange(value, "pcName")}
                            options={pcOptions}
                            disabled={!inputValues.state}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                              option.label.toLowerCase().includes(input.toLowerCase())
                            }
                            placeholder={
                              inputValues.state ? "Select Parliamentary" : "Please select state first"
                            }
                          />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>

                  <Row className="add-modal-row mb-2">
                    <Col span={8} offset={2}>
                      <label>
                        Year<span className="required-field">*</span>
                      </label>
                      <Form.Item
                        name="year"
                        rules={[{ required: true, message: "Please Select Year" }]}
                      >
                        <Select
                          showSearch
                          onChange={(value) => handleChange(value, "year")}
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children.toString().toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          {years.map((year) => (
                            <Option key={year} value={year}>
                              {year}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row className="add-modal-row mb-2">
                    <Col span={18}>
                      <label>
                        Description<span className="required-field">*</span>
                      </label>
                      <Form.Item
                        name="description"
                        rules={[{ required: true, message: "Please enter the Description" }]}
                      >
                        <TextArea
                          rows={2}
                          name="description"
                          onChange={(e) => handleChange(e.target.value, "description")}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row className="add-modal-row mb-2">
                    <Col span={22}>
                      <h3>
                        Ward-wise Shape File (GeoJSON)<span className="required-field">*</span>
                      </h3>
                      <Form.Item
                        name="boundaries"
                        rules={[{ required: true, message: "Please upload the shape file" }]}
                      >
                        <div className="constituency-upload-file">
                          <Upload.Dragger
                            key="select-file"
                            name="boundaries"
                            beforeUpload={() => false}
                            fileList={fileList}
                            onChange={(info) => {
                              const { file, fileList: newFileList } = info;
                              if (file.status === "done") {
                                setFileList([file]);
                              } else {
                                setFileList(newFileList);
                              }
                              handleChange(newFileList, "boundaries");
                            }}
                            className="custom-file-upload"
                            accept=".json"
                          >
                            {inputValues?.boundaries.length > 0 ? (
                              <div className="fileUpload">
                                <div className="text-right">
                                  <button
                                    onClick={handleResetFile}
                                    className="reset-button font-bold"
                                  >
                                    <AiOutlineClose />
                                  </button>
                                </div>
                                <div className="flex file-icon">
                                  <FileFilled />
                                  <p>{inputValues?.boundaries[0]?.name}</p>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <img src={uploadCloud} alt="" className="m-auto" />
                                <p className="ant-upload-text">Select a file or drag and drop</p>
                                <p className="ant-upload-text">GeoJSON file size no more than 10MB</p>
                                <span className="select-file">Select File</span>
                              </div>
                            )}
                          </Upload.Dragger>
                        </div>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row className="add-modal-row mb-2">
                    <Col span={22}>
                      <h3>
                        Upload Quantitative Survey Excel<span className="required-field">*</span>
                      </h3>
                      <Upload.Dragger
                        beforeUpload={(file) => {
                          setExcelFile(file);
                          return false;
                        }}
                        onRemove={() => setExcelFile(null)}
                        fileList={excelFile ? [excelFile] : []}
                        accept=".xlsx,.xls,.csv"
                        className="custom-file-upload"
                      >
                        {excelFile ? (
                          <div className="fileUpload">
                            <div className="flex file-icon">
                              <FileFilled />
                              <p>{excelFile.name}</p>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <img src={uploadCloud} alt="" className="m-auto" />
                            <p>Select or drag Excel file</p>
                            <span className="select-file">Select File</span>
                          </div>
                        )}
                      </Upload.Dragger>
                    </Col>
                  </Row>

                  {excelFile && (
                    <div style={{ textAlign: "right", marginTop: "16px" }}>
                      <button
                        type="button"
                        className="add-user-btn"
                        onClick={handleExcelUpload}
                        disabled={uploadingExcel}
                      >
                        {uploadingExcel ? "Uploading..." : "Upload Excel"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default AddQuantitativeSurvey;
